import { useEffect, useRef, useState } from 'react';
import { Logo, Menu, MenuItem, Row, TopNav, type TopNavItem, Typography } from '@tempmee/react';
import { OutlineChevronDownIcon as ChevronDownIcon } from '@tempmee/react-icons';
import { colors, shadows } from '@tempmee/tailwind';
import { NavLink, useLocation } from 'react-router';
import { Links } from '~/constants';
import { fireLoginTabClicked, fireSignupTabClicked } from '~/posthogs/events/Signup';
import { useCancelPartialSession } from '../useCancelPartialSession';

const INACTIVE_COLOR = colors.shades[0];

const EXPLORE_ITEMS = [
  { label: 'Dental Office', href: Links.temporaryStaffing },
  { label: 'Dental Professional', href: Links.hygienist },
] as const;

const NAV_ITEM_PADDING = '18px 16px';

// TODO: replace with DS Dropdown/Menu component once available in @tempmee/react.
// Rendered in TopNav's `endSlot` since it isn't a standard tab item.
const ExploreDropdown = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        <Row as="span" align="center" style={{ padding: NAV_ITEM_PADDING, gap: 2 }}>
          <Typography
            as="span"
            variant="subtitle1"
            color={INACTIVE_COLOR}
            style={{ whiteSpace: 'nowrap' }}
          >
            Explore GoTu
          </Typography>
          <ChevronDownIcon size={24} color={INACTIVE_COLOR} />
        </Row>
      </button>

      {open && (
        <Menu style={{ position: 'absolute', top: '100%', right: 0, zIndex: 10, minWidth: 180 }}>
          {EXPLORE_ITEMS.map((item) => (
            <MenuItem key={item.href} href={item.href} target="_blank" rel="noreferrer noopener">
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </div>
  );
};

export interface IAuthHeaderNavProps {
  /**
   * Header chrome variant. `default` shows Log In + Sign Up nav items;
   * `forgot-password` hides those and surfaces "Forgot Password?" as the
   * active item. @default 'default'
   */
  variant?: 'default' | 'forgot-password';
}

// `to` is forwarded by the underlying Tab to the `NavLink` component but isn't
// part of TabProps' typed surface, so widen the item type locally.
type TAuthNavItem = TopNavItem & { to?: string };

export const AuthHeaderNav = ({ variant = 'default' }: IAuthHeaderNavProps = {}) => {
  const cancelPartialSession = useCancelPartialSession();
  const location = useLocation();
  const showForgotPasswordLabel = variant === 'forgot-password';

  const handleLoginClick = () => {
    fireLoginTabClicked();
    cancelPartialSession();
  };

  const handleSignupClick = () => {
    fireSignupTabClicked();
  };

  const activeValue = showForgotPasswordLabel
    ? 'forgot'
    : location.pathname === '/signup'
      ? 'signup'
      : location.pathname === '/login'
        ? 'login'
        : undefined;

  const items: TAuthNavItem[] = showForgotPasswordLabel
    ? [{ value: 'forgot', label: 'Forgot Password?' }]
    : [
        {
          value: 'login',
          label: 'Log In',
          component: NavLink,
          to: '/login',
          onClick: handleLoginClick,
        },
        {
          value: 'signup',
          label: 'Sign Up',
          component: NavLink,
          to: '/signup',
          onClick: handleSignupClick,
        },
      ];

  return (
    <TopNav
      color="dark"
      data-testid="auth-header-nav"
      style={{ boxShadow: shadows['elevation-1'] }}
      value={activeValue}
      items={items}
      logo={<Logo tone="light" mark="full" height={44} />}
      logoComponent={NavLink}
      logoProps={{ to: '/login', onClick: cancelPartialSession }}
      endSlot={<ExploreDropdown />}
    />
  );
};
